import {
  ArrowRight,
  Check,
  CheckCircle2,
  Coins,
  LockKeyhole,
  Pencil,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Permission } from "@loyalty/shared";
import { useState, type SyntheticEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  type BusinessRedemptionRule,
  type BusinessRedemptionRuleInput,
  type BusinessSetupStatus,
  type TenantRewardPointsProgram,
  type TenantRewardPointsProgramUpdateInput,
  fetchBusinessRedemptionRules,
  fetchBusinessSetupStatus,
  fetchTenantInventory,
  messageFromError,
  publishTenantRewardPointsProgramDraft,
  saveTenantRewardPointsProgramDraft,
  updateBusinessRedemptionRule,
} from "../api";
import { AppAlert } from "../components/AppAlertDialog";
import { businessSetupQueryKey } from "../business-setup-gate";
import {
  type BusinessSetupConfirmation,
  businessSetupWizardProgress,
  businessSetupWizardStage,
} from "../business-setup-wizard";
import { QuickOfferBuilder, tenantRewardCapabilities } from "./TenantRewardsWorkspace";

const earningQueryKeys = [
  ["tenant-reward-points-program"],
  ["tenant-reward-points-program-editable"],
  ["tenant-reward-point-rules"],
] as const;
const redemptionRulesKey = ["business-redemption-rules"] as const;
const inventoryKey = ["tenant-reward-inventory"] as const;

const formatNumber = (value: number) => new Intl.NumberFormat("en-US").format(value);
const formatMoney = (minor: number, currency = "NPR") =>
  new Intl.NumberFormat("en-NP", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(minor / 100);

const confirmationFrom = (value: string | null): BusinessSetupConfirmation =>
  value === "earning" || value === "redemption" || value === "complete" ? value : null;

function SetupAccessMessage({ children }: { children: string }) {
  return (
    <section className="business-setup-wizard__access" role="alert">
      <LockKeyhole />
      <div>
        <h2>Setup access is required</h2>
        <p>{children}</p>
      </div>
    </section>
  );
}

function EarningRuleStep({
  onPublished,
}: {
  onPublished: () => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [spendUnit, setSpendUnit] = useState("100");
  const [points, setPoints] = useState("1");
  const [minimumSpend, setMinimumSpend] = useState("100");
  const [maximumPoints, setMaximumPoints] = useState("5000");
  const [expirationDays, setExpirationDays] = useState("");
  const [savedDraft, setSavedDraft] = useState<TenantRewardPointsProgram | null>(null);
  const saveMutation = useMutation({ mutationFn: saveTenantRewardPointsProgramDraft });
  const publishMutation = useMutation({ mutationFn: publishTenantRewardPointsProgramDraft });

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const input: TenantRewardPointsProgramUpdateInput = {
      spendUnitMinor: Math.round(Number(spendUnit) * 100),
      pointsPerUnit: Math.round(Number(points)),
      minimumQualifyingMinor: Math.round(Number(minimumSpend) * 100),
      maximumPointsPerTransaction: maximumPoints ? Math.round(Number(maximumPoints)) : null,
      pointExpirationDays: expirationDays ? Math.round(Number(expirationDays)) : null,
    };
    try {
      const draft = await saveMutation.mutateAsync(input);
      await Promise.all(
        earningQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
      setSavedDraft(draft);
    } catch {
      // The sanitized API error is rendered below.
    }
  };

  const publish = async () => {
    if (!savedDraft) return;
    try {
      await publishMutation.mutateAsync(savedDraft.publicId);
      await onPublished();
      await Promise.all(
        earningQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
    } catch {
      // The sanitized API error is rendered below.
    }
  };

  if (savedDraft) {
    return (
      <section className="business-setup-card business-setup-review" aria-labelledby="earning-review-title">
        <div className="business-setup-card__heading">
          <span><ShieldCheck /></span>
          <div>
            <p className="eyebrow">REVIEW EARNING RULE</p>
            <h2 id="earning-review-title">Publish this earning rule?</h2>
            <p>The draft is saved. Review the values before making it active.</p>
          </div>
        </div>
        {publishMutation.isError && <AppAlert message={messageFromError(publishMutation.error)} />}
        <dl className="business-setup-review__values">
          <div><dt>Earning rate</dt><dd>{formatNumber(savedDraft.pointsPerUnit)} point{savedDraft.pointsPerUnit === 1 ? "" : "s"} per {formatMoney(savedDraft.spendUnitMinor, savedDraft.currencyCode)}</dd></div>
          <div><dt>Minimum purchase</dt><dd>{formatMoney(savedDraft.minimumQualifyingMinor, savedDraft.currencyCode)}</dd></div>
          <div><dt>Points limit</dt><dd>{savedDraft.maximumPointsPerTransaction ? `${formatNumber(savedDraft.maximumPointsPerTransaction)} per purchase` : "No limit"}</dd></div>
          <div><dt>Expiration</dt><dd>{savedDraft.pointExpirationDays ? `${String(savedDraft.pointExpirationDays)} days` : "Points do not expire"}</dd></div>
        </dl>
        <div className="business-setup-actions">
          <button className="button button--outline" type="button" onClick={() => { setSavedDraft(null); }}>
            <Pencil /> Edit draft
          </button>
          <button className="button button--primary" disabled={publishMutation.isPending} type="button" onClick={() => { void publish(); }}>
            <ShieldCheck /> {publishMutation.isPending ? "Publishing…" : "Publish earning rule"}
          </button>
        </div>
      </section>
    );
  }

  return (
    <form className="business-setup-card" onSubmit={(event) => { void submit(event); }}>
      <div className="business-setup-card__heading">
        <span><Coins /></span>
        <div>
          <p className="eyebrow">STEP 1 · EARNING RULE</p>
          <h2>Choose how customers earn points</h2>
          <p>Start with clear defaults, then review and publish the rule yourself.</p>
        </div>
      </div>
      {saveMutation.isError && <AppAlert message={messageFromError(saveMutation.error)} />}
      <div className="business-setup-form-grid">
        <label>
          <span>Spend unit (NPR)</span>
          <input required min="0.01" step="0.01" type="number" value={spendUnit} onChange={(event) => { setSpendUnit(event.target.value); }} />
          <small>The purchase amount represented by one earning unit.</small>
        </label>
        <label>
          <span>Points per unit</span>
          <input required min="1" max="1000000" step="1" type="number" value={points} onChange={(event) => { setPoints(event.target.value); }} />
          <small>Points awarded for every full spend unit.</small>
        </label>
        <label>
          <span>Minimum qualifying purchase (NPR)</span>
          <input required min="0" step="0.01" type="number" value={minimumSpend} onChange={(event) => { setMinimumSpend(event.target.value); }} />
        </label>
        <label>
          <span>Maximum points per purchase</span>
          <input min="1" step="1" type="number" value={maximumPoints} onChange={(event) => { setMaximumPoints(event.target.value); }} placeholder="No limit" />
        </label>
        <label>
          <span>Point expiration days</span>
          <input min="1" max="3650" step="1" type="number" value={expirationDays} onChange={(event) => { setExpirationDays(event.target.value); }} placeholder="Never expires" />
        </label>
      </div>
      <div className="business-setup-summary">
        <ShieldCheck />
        <div>
          <strong>{formatNumber(Number(points) || 0)} point{Number(points) === 1 ? "" : "s"} per {formatMoney(Math.round((Number(spendUnit) || 0) * 100))}</strong>
          <span>Saving creates a draft only. You will review and publish it next.</span>
        </div>
      </div>
      <div className="business-setup-actions business-setup-actions--end">
        <button className="button button--primary" disabled={saveMutation.isPending} type="submit">
          {saveMutation.isPending ? "Saving draft…" : "Save draft and review"} <ArrowRight />
        </button>
      </div>
    </form>
  );
}

function RedemptionRuleForm({
  rule,
  onPublished,
}: {
  rule?: BusinessRedemptionRule | undefined;
  onPublished: () => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [pointsPerUnit, setPointsPerUnit] = useState(String(rule?.pointsPerUnit ?? 100));
  const [valueRupees, setValueRupees] = useState(String((rule?.valuePerUnitMinor ?? 100) / 100));
  const [minimumPoints, setMinimumPoints] = useState(String(rule?.minimumRedemptionPoints ?? 100));
  const [maximumPoints, setMaximumPoints] = useState(rule?.maximumRedemptionPointsPerTransaction?.toString() ?? "");
  const [reviewing, setReviewing] = useState(false);
  const publishMutation = useMutation({ mutationFn: updateBusinessRedemptionRule });

  const input = (): BusinessRedemptionRuleInput => ({
    pointsPerUnit: Math.round(Number(pointsPerUnit)),
    valuePerUnitMinor: Math.round(Number(valueRupees) * 100),
    minimumRedemptionPoints: Math.round(Number(minimumPoints)),
    maximumRedemptionPointsPerTransaction: maximumPoints ? Math.round(Number(maximumPoints)) : null,
  });

  const review = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setReviewing(true);
  };

  const publish = async () => {
    try {
      await publishMutation.mutateAsync(input());
      await onPublished();
      await queryClient.invalidateQueries({ queryKey: redemptionRulesKey });
    } catch {
      // The sanitized API error is rendered below.
    }
  };

  if (reviewing) {
    const reviewed = input();
    return (
      <section className="business-setup-card business-setup-review" aria-labelledby="redemption-review-title">
        <div className="business-setup-card__heading">
          <span><ShieldCheck /></span>
          <div>
            <p className="eyebrow">REVIEW REDEMPTION RULE</p>
            <h2 id="redemption-review-title">Publish this redemption rule?</h2>
            <p>This conversion applies only to Reward Points earned at this business.</p>
          </div>
        </div>
        {publishMutation.isError && <AppAlert message={messageFromError(publishMutation.error)} />}
        <dl className="business-setup-review__values">
          <div><dt>Conversion</dt><dd>{formatNumber(reviewed.pointsPerUnit)} points = {formatMoney(reviewed.valuePerUnitMinor)}</dd></div>
          <div><dt>Minimum redemption</dt><dd>{formatNumber(reviewed.minimumRedemptionPoints)} points</dd></div>
          <div><dt>Checkout limit</dt><dd>{reviewed.maximumRedemptionPointsPerTransaction ? `${formatNumber(reviewed.maximumRedemptionPointsPerTransaction)} points` : "No limit"}</dd></div>
        </dl>
        <div className="business-setup-actions">
          <button className="button button--outline" type="button" onClick={() => { setReviewing(false); }}><Pencil /> Edit rule</button>
          <button className="button button--primary" disabled={publishMutation.isPending} type="button" onClick={() => { void publish(); }}><ShieldCheck /> {publishMutation.isPending ? "Publishing…" : "Publish redemption rule"}</button>
        </div>
      </section>
    );
  }

  return (
    <form className="business-setup-card" onSubmit={review}>
      <div className="business-setup-card__heading">
        <span><RefreshCw /></span>
        <div>
          <p className="eyebrow">STEP 2 · REDEMPTION RULE</p>
          <h2>Set the value customers can use</h2>
          <p>Define how this business converts its own Reward Points at checkout.</p>
        </div>
      </div>
      <div className="business-setup-form-grid">
        <label><span>Reward Points</span><input required min="1" step="1" type="number" value={pointsPerUnit} onChange={(event) => { setPointsPerUnit(event.target.value); }} /><small>Points used for one conversion unit.</small></label>
        <label><span>Value (NPR)</span><input required min="0.01" step="0.01" type="number" value={valueRupees} onChange={(event) => { setValueRupees(event.target.value); }} /><small>Discount produced by each conversion unit.</small></label>
        <label><span>Minimum points per redemption</span><input required min="0" step="1" type="number" value={minimumPoints} onChange={(event) => { setMinimumPoints(event.target.value); }} /></label>
        <label><span>Maximum points per checkout</span><input min="1" step="1" type="number" value={maximumPoints} onChange={(event) => { setMaximumPoints(event.target.value); }} placeholder="No limit" /></label>
      </div>
      <div className="business-setup-summary">
        <ShieldCheck />
        <div><strong>{formatNumber(Number(pointsPerUnit) || 0)} points = {formatMoney(Math.round((Number(valueRupees) || 0) * 100))}</strong><span>Review this conversion before publishing it.</span></div>
      </div>
      <div className="business-setup-actions business-setup-actions--end">
        <button className="button button--primary" type="submit">Review redemption rule <ArrowRight /></button>
      </div>
    </form>
  );
}

function RedemptionRuleStep({ onPublished }: { onPublished: () => Promise<void> }) {
  const query = useQuery({ queryKey: redemptionRulesKey, queryFn: fetchBusinessRedemptionRules });
  if (query.isLoading) return <div className="business-setup-wizard__loading">Loading redemption defaults…</div>;
  if (query.isError) return <AppAlert message={messageFromError(query.error)} />;
  const active = query.data?.find((rule) => rule.status === "active") ?? query.data?.[0];
  return <RedemptionRuleForm key={active?.publicId ?? "new-redemption-rule"} rule={active} onPublished={onPublished} />;
}

function SetupConfirmation({
  eyebrow,
  title,
  description,
  action,
  onContinue,
}: {
  eyebrow: string;
  title: string;
  description: string;
  action: string;
  onContinue: () => void;
}) {
  return (
    <section className="business-setup-card business-setup-confirmation" aria-live="polite">
      <span className="business-setup-confirmation__icon"><CheckCircle2 /></span>
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      <p>{description}</p>
      <button className="button button--primary" type="button" onClick={onContinue}>{action} <ArrowRight /></button>
    </section>
  );
}

function QuickOfferStep({
  permissions,
  onPublished,
}: {
  permissions: Permission[];
  onPublished: () => Promise<void>;
}) {
  const capabilities = tenantRewardCapabilities(permissions);
  const inventory = useQuery({
    queryKey: inventoryKey,
    queryFn: fetchTenantInventory,
    enabled: capabilities.canRead,
  });
  if (!capabilities.canRead || !capabilities.canCreate || !capabilities.canPublish) {
    return <SetupAccessMessage>Your role needs reward read, create, and publish permissions to complete the first Quick Offer.</SetupAccessMessage>;
  }
  if (inventory.isLoading) return <div className="business-setup-wizard__loading">Loading Quick Offer tools…</div>;
  if (inventory.isError) return <AppAlert message={messageFromError(inventory.error)} />;
  return (
    <QuickOfferBuilder
      inventory={inventory.data ?? []}
      canPublish
      canSubmit={capabilities.canEdit}
      embedded
      setupMode
      onClose={() => undefined}
      onSaved={onPublished}
    />
  );
}

export function BusinessSetupWizard({
  permissions,
  tenantName,
}: {
  permissions: Permission[];
  tenantName: string;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transitionConfirmation, setTransitionConfirmation] =
    useState<BusinessSetupConfirmation>(null);
  const setup = useQuery({
    queryKey: businessSetupQueryKey,
    queryFn: fetchBusinessSetupStatus,
  });
  const confirmation = transitionConfirmation
    ?? confirmationFrom(searchParams.get("confirmation"));

  const showConfirmation = (next: Exclude<BusinessSetupConfirmation, null>) => {
    setTransitionConfirmation(next);
    setSearchParams({ confirmation: next }, { replace: true });
  };
  const continueSetup = () => {
    setTransitionConfirmation(null);
    setSearchParams({}, { replace: true });
  };
  const refreshSetup = async () => {
    await queryClient.invalidateQueries({ queryKey: businessSetupQueryKey });
    const refreshed = await setup.refetch();
    if (!refreshed.data) throw refreshed.error ?? new Error("Business Account Setup status is unavailable.");
    return refreshed.data;
  };

  if (setup.isLoading) return <main className="portal-content"><div className="business-setup-wizard__loading">Preparing your account setup…</div></main>;
  if (setup.isError || !setup.data) {
    return (
      <main className="portal-content">
        <section className="business-setup-wizard__access">
          <ShieldCheck />
          <div><h1>We could not load account setup</h1><p>{messageFromError(setup.error)}</p><button className="button button--primary" type="button" onClick={() => { void setup.refetch(); }}><RefreshCw /> Try again</button></div>
        </section>
      </main>
    );
  }

  const current: BusinessSetupStatus = setup.data;
  const progress = businessSetupWizardProgress(current);
  const stage = businessSetupWizardStage(current, confirmation);
  const currentStep = stage === "earning_rule" || stage === "earning_confirmation"
    ? 0
    : stage === "redemption_rule" || stage === "redemption_confirmation"
      ? 1
      : 2;
  const steps = [
    { label: "Earning rule", detail: "How customers earn", complete: progress.earning },
    { label: "Redemption rule", detail: "How customers use points", complete: progress.redemption },
    { label: "First Quick Offer", detail: "Your first live reward", complete: progress.quickOffer },
  ];

  return (
    <main className="portal-content business-setup-wizard">
      <header className="business-setup-wizard__hero">
        <div>
          <p className="eyebrow">BUSINESS ACCOUNT SETUP</p>
          <h1>Build your loyalty foundation</h1>
          <p><strong>{tenantName}</strong><span>Complete these three steps in order. Nothing is published until you review and confirm it.</span></p>
        </div>
        <span><Sparkles /></span>
      </header>

      <ol className="business-setup-wizard__progress" aria-label="Business setup progress">
        {steps.map((step, index) => {
          const status = step.complete ? "is-complete" : index === currentStep ? "is-current" : "is-locked";
          return (
            <li className={status} key={step.label} aria-current={index === currentStep ? "step" : undefined}>
              <span>{step.complete ? <Check /> : index + 1}</span>
              <div><strong>{step.label}</strong><small>{step.detail}</small></div>
              {index > currentStep && !step.complete && <LockKeyhole />}
            </li>
          );
        })}
      </ol>

      <section className="business-setup-wizard__stage" aria-label={`Setup step ${String(currentStep + 1)} of 3`}>
        {stage === "earning_rule" && (
          permissions.includes("reward.points.configure")
            ? <EarningRuleStep onPublished={async () => { showConfirmation("earning"); await refreshSetup(); }} />
            : <SetupAccessMessage>Your role needs permission to configure Reward Point rules.</SetupAccessMessage>
        )}
        {stage === "earning_confirmation" && <SetupConfirmation eyebrow="EARNING RULE PUBLISHED" title="Your earning rule is active" description="Continue when you are ready to define how customers redeem those points." action="Continue to redemption rule" onContinue={continueSetup} />}
        {stage === "redemption_rule" && (
          permissions.includes("reward.points.configure")
            ? <RedemptionRuleStep onPublished={async () => { showConfirmation("redemption"); await refreshSetup(); }} />
            : <SetupAccessMessage>Your role needs permission to configure Reward Point rules.</SetupAccessMessage>
        )}
        {stage === "redemption_confirmation" && <SetupConfirmation eyebrow="REDEMPTION RULE PUBLISHED" title="Customers now know what their points are worth" description="Continue to publish one simple offer customers can discover and use." action="Continue to first Quick Offer" onContinue={continueSetup} />}
        {stage === "quick_offer" && <QuickOfferStep permissions={permissions} onPublished={async () => { showConfirmation("complete"); await refreshSetup(); }} />}
        {stage === "complete" && <SetupConfirmation eyebrow="ACCOUNT SETUP COMPLETE" title="Your business is ready for customers" description="The earning rule, redemption rule, and first Quick Offer are live. Your full Business Owner workspace is now unlocked." action="Open business workspace" onContinue={() => { void navigate("/tenant", { replace: true }); }} />}
      </section>
    </main>
  );
}
